import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { retryFailed, uploadStore } from "@/lib/upload";

const fmt = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} Go` : b >= 1024 ** 2 ? `${(b / 1024 ** 2).toFixed(0)} Mo` : `${Math.ceil(b / 1024)} Ko`);

const FailedList = ({ failed }) => (
  <div className="max-h-28 overflow-y-auto text-xs text-red-700 space-y-1" data-testid="upload-panel-failed-list">
    {failed.slice(0, 50).map((f, i) => <div key={i} className="truncate" title={f.error}>{f.file.name} — {f.error}</div>)}
  </div>
);

export const UploadPanel = () => {
  const [s, setS] = useState(uploadStore.get());
  const [open, setOpen] = useState(true);
  useEffect(() => uploadStore.subscribe(setS), []);
  if (!s.total) return null;
  const pct = s.bytesTotal ? Math.round((s.bytesDone / s.bytesTotal) * 100) : 100;
  const current = Object.values(s.current);
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-lg border bg-white shadow-xl p-4 space-y-3" data-testid="upload-panel">
      <div className="flex items-center gap-2">
        {s.active ? <Loader2 className="h-4 w-4 animate-spin text-sky-700" /> : s.failed.length ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        <span className="font-medium text-sm truncate flex-1" data-testid="upload-panel-title">{s.label}</span>
        <button className="text-xs text-slate-500 hover:text-slate-900" onClick={() => setOpen(!open)} data-testid="upload-panel-toggle">{open ? "Réduire" : "Détails"}</button>
        {!s.active && <button onClick={uploadStore.dismiss} aria-label="Fermer" data-testid="upload-panel-close"><X className="h-4 w-4 text-slate-500" /></button>}
      </div>
      <Progress value={pct} data-testid="upload-panel-progress" />
      <div className="flex justify-between text-xs text-slate-600" data-testid="upload-panel-stats">
        <span>{s.done} / {s.total} fichiers{s.skipped ? ` (${s.skipped} déjà en ligne)` : ""}</span>
        <span>{fmt(s.bytesDone)} / {fmt(s.bytesTotal)} · {pct} %</span>
      </div>
      {open && current.length > 0 && (
        <div className="space-y-1" data-testid="upload-panel-current">
          {current.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-700"><UploadCloud className="h-3 w-3 shrink-0" /><span className="truncate flex-1">{c.name}</span><span>{c.pct} %</span></div>
          ))}
        </div>
      )}
      {s.active && <p className="text-[11px] text-slate-500">Gardez cet onglet ouvert. En cas de coupure, redéposez le même dossier : l'envoi reprend là où il s'est arrêté.</p>}
      {s.failed.length > 0 && !s.active && (
        <div className="space-y-2">
          {open && <FailedList failed={s.failed} />}
          <Button size="sm" variant="outline" className="w-full" onClick={retryFailed} data-testid="upload-panel-retry-btn"><RotateCcw className="h-3 w-3 mr-2" />Réessayer les {s.failed.length} échec(s)</Button>
        </div>
      )}
    </div>
  );
};
